import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { dateToStringFormat } from '@common/utils/date';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { selectSelectedRobotId } from 'store/robots/robots-slice-selectors';
import { getErrors } from 'store/local-robot/local-robot-actions';
import { selectErrors, selectErrorsRestSet } from 'store/local-robot/local-robot-slice-selectors';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import ModelName from '@common/enums/robot/model-to-translation-string';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

const jsonStyle = {
  m: 0,
  p: 2,
  borderRadius: 2,
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  fontSize: 13,
  overflow: 'auto',
  maxHeight: 320,
};

const ErrorsInfo: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const selectedRobotId = useAppSelector((state) => selectSelectedRobotId(state));
  const robotErrors = useAppSelector((state) => selectErrors(state));
  const robotErrorsRest = useAppSelector((state) => selectErrorsRestSet(state));

  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (robotErrorsRest.getStatus === HttpFetchStatus.Idle && selectedRobotId != null) {
      dispatch(getErrors({ robotId: selectedRobotId, page: 1 }));
    }
  }, [dispatch, robotErrorsRest.getStatus, selectedRobotId]);

  if (robotErrors == null) {
    return null;
  }

  const hasMoreErrors = robotErrors.data.length < robotErrors.count;

  const loadMoreHandler = () => {
    if (
      selectedRobotId == null ||
      robotErrors == null ||
      robotErrorsRest.getStatus === HttpFetchStatus.Loading
    ) {
      return;
    }

    dispatch(
      getErrors({
        robotId: selectedRobotId,
        page: (robotErrors.page ?? 1) + 1,
      }),
    );
  };

  const selectedError = robotErrors.data.find((e) => e.id === selectedErrorId);

  return (
    <React.Fragment>
      <CustomAlert header={t('info-resolving-validation-errors')} severity="info" />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('date-label')}</TableCell>
              <TableCell>{t('validation-error-label')}</TableCell>
              <TableCell>{t('validation-error-object-type-name')}</TableCell>
              <TableCell>{t('validation-error-object-name')}</TableCell>
              <TableCell>
                {/* this translates to Map the backend has room for this */}
                {t('room')}
              </TableCell>
              <TableCell>
                {/* and this to room the backend has floor for this*/}
                {t('label-room')}
              </TableCell>
              <TableCell>{t('data-version-label')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {robotErrors.data.map((e) => (
              <React.Fragment key={e.id}>
                <TableRow>
                  <TableCell>{dateToStringFormat(DateTime.fromISO(e.timestamp))}</TableCell>
                  <TableCell>
                    {Object.values(e.validationError)
                      .flat()
                      .map((err) => (
                        <div key={err}>{t(err)}</div>
                      ))}
                  </TableCell>
                  <TableCell>
                    {/* every model needs to be tranlated too! */}
                    {t(ModelName[e.model as keyof typeof ModelName] ?? e.model)}
                  </TableCell>
                  <TableCell>{e.instanceName ?? t('no-name-found')}</TableCell>
                  <TableCell>{e.roomName ?? t('no-room-found')}</TableCell>
                  <TableCell>{e.floorName ?? t('no-floor-found')}</TableCell>
                  <TableCell>{e.dataVersion}</TableCell>
                  <TableCell>
                    <Button variant="outlined" onClick={() => setSelectedErrorId(e.id)}>
                      {t('view-details-label')}
                    </Button>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack alignItems="center" sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={loadMoreHandler}
          disabled={robotErrorsRest.getStatus === HttpFetchStatus.Loading || !hasMoreErrors}
        >
          {robotErrorsRest.getStatus === HttpFetchStatus.Loading
            ? t('loading')
            : hasMoreErrors
              ? t('load-more-label')
              : t('no-more-errors-to-load-label')}
        </Button>
      </Stack>

      <DialogWrapperWithClose
        title={t('validation-error-details-title')}
        open={selectedErrorId !== null}
        onClose={() => setSelectedErrorId(null)}
        PaperProps={{
          sx: {
            maxWidth: 'none',
            width: 'fitContent',
          },
        }}
      >
        <DialogContent sx={{ px: 3, py: 2 }}>
          {/* Top section */}
          <Stack spacing={2} mb={3}>
            <Stack spacing={1}>
              {Object.values(selectedError?.validationError ?? {})
                .flat()
                .map((err) => (
                  <CustomAlert header={t(err)} severity="warning" />
                ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('data-version-label')}:</strong> {selectedError?.dataVersion ?? '-'}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Incoming data */}
            <Box flex={1} display="flex" flexDirection="column">
              {/* Header */}
              <Box
                sx={{
                  minHeight: 48,
                  display: 'flex',
                  alignItems: 'flex-end',
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 13 }}>
                  {t('recieved-data-label')}:{' '}
                  {t(
                    ModelName[selectedError?.model as keyof typeof ModelName] ??
                      selectedError?.model,
                  )}
                </Typography>
              </Box>

              {/* Content */}
              <Box component="pre" sx={jsonStyle}>
                {JSON.stringify(selectedError?.incomingData, null, 2)}
              </Box>
            </Box>

            {/* DB data */}
            <Box flex={1} display="flex" flexDirection="column">
              {/* Header */}
              <Box
                sx={{
                  minHeight: 48,
                  display: 'flex',
                  alignItems: 'flex-end',
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 13 }}>
                  {t('data-in-db-label')}
                </Typography>
              </Box>

              {/* Content */}
              <Box component="pre" sx={jsonStyle}>
                {JSON.stringify(selectedError?.instanceData, null, 2)}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </DialogWrapperWithClose>
    </React.Fragment>
  );
};

export default ErrorsInfo;
